import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ColDef, GridOptions, PaginationChangedEvent, PaginationNumberFormatterParams } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { iif, of } from 'rxjs';
import { concatMap, finalize, map } from 'rxjs/operators';
import { tryGetErrorMessage } from '../../../../../error.utils';
import { defaultTableDefOptions } from '../../../../../helpers';
import { AssignmentService } from '../../../../../service/assignment.service';
import { BranchService } from '../../../../../service/branch.service';
import { LossPreventionService } from '../../../../../service/loss-prevention.service';
import { NotifyService } from '../../../../../service/notify.service';
import {
  Branch,
  CreateCancellationItemRequest,
  CreateCancellationRequest,
  LocationTiny,
  ReviewAssignmentDto,
  ReviewSubjectType,
} from '../../../../../types';
import { Kiosk, KioskTransaction } from '../../../../../types/impl';
import { generateUuid as generateUuidUtil } from '../../../../../utils';
import { PageSelectLocationComponent } from '../../../../components/page-select-location/page-select-location.component';
import { LocationService } from '../../../../../service/location.service';

@Component({
  selector: 'app-create-review',
  templateUrl: './create-review.component.html',
  styleUrl: './create-review.component.scss',
  standalone: false,
})
export class LossPreventionAdminCreateReviewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private assignmentService = inject(AssignmentService);
  private lpService = inject(LossPreventionService);
  private notify = inject(NotifyService);
  private branchService = inject(BranchService);
  private locationService = inject(LocationService);
  private locationSelect = viewChild<PageSelectLocationComponent>('locationSelect');

  public branch = signal<Branch | undefined>(undefined);
  public location = signal<LocationTiny | undefined>(undefined);

  public branchAlias = signal<string>('');
  public locationAlias = signal<string>('');
  public selectedBranchAliasId = signal<number | undefined>(undefined);
  public selectedKioskId = signal<number | undefined>(undefined);
  public uniqueId = signal<string>('');
  public kiosks = signal<Kiosk[]>([]);
  public branchAliasOptions = signal<Array<{ id: number; name: string }>>([]);
  public locationAliasOptions = signal<
    Array<{
      id: number;
      name: string;
      branch_alias_id: number;
      branch_alias_name: string;
    }>
  >([]);
  public isLoadingBranchAlias = signal<boolean>(false);
  public isLoadingLocationAlias = signal<boolean>(false);
  private branchIdParam = signal<number | undefined>(undefined);
  private locationIdParam = signal<number | undefined>(undefined);
  private branchAliasIdParam = signal<number | undefined>(undefined);
  private locationAliasIdParam = signal<number | undefined>(undefined);
  private hasAppliedQueryParams = signal<boolean>(false);
  public autoSelectBranchIds = computed(() => (this.branchIdParam() ? [this.branchIdParam() as number] : []));
  public autoSelectLocationIds = computed(() => (this.locationIdParam() ? [this.locationIdParam() as number] : []));
  private lastBranchId = signal<number | undefined>(undefined);
  private lastLocationId = signal<number | undefined>(undefined);
  private lastBranchAliasId = signal<number | undefined>(undefined);
  private lastKioskId = signal<number | undefined>(undefined);

  private shouldPreserveAliasParamsOnInitForBranch(branchId: number) {
    return (
      !this.hasAppliedQueryParams() &&
      this.branchIdParam() === branchId &&
      (this.branchAliasIdParam() !== undefined || this.locationAliasIdParam() !== undefined)
    );
  }

  private shouldPreserveAliasParamsOnInitForLocation(locationId: number) {
    return (
      !this.hasAppliedQueryParams() &&
      this.locationIdParam() === locationId &&
      (this.branchAliasIdParam() !== undefined || this.locationAliasIdParam() !== undefined)
    );
  }

  public cancelDateDate = signal<Date>(new Date());
  public cancelTime = signal<string>(this.formatTime(new Date()));
  public theftScore = signal<number>(1);
  public theftCategory = signal<'low' | 'medium' | 'high'>('high');
  public theftCategoryOptions: Array<'low' | 'medium' | 'high'> = ['low', 'medium', 'high'];
  public faceSimilarity = signal<boolean>(true);
  public isCreatingCancellation = signal<boolean>(false);
  public isCreatingReview = signal<boolean>(false);
  public isLoadingRecent = signal<boolean>(false);
  public items = signal<CreateCancellationItemRequest[]>([]);
  public createdCancellationId = signal<number | undefined>(undefined);
  public createdReviewId = signal<string | undefined>(undefined);

  public photoRows = signal<Array<{ file?: File; previewUrl?: string }>>([]);
  public recentWalkInFiles = signal<File[]>([]);

  // Video upload (single file)
  public videoFile = signal<File | null>(null);
  public videoOffsetSeconds = signal<number | undefined>(undefined);

  public branchId = computed(() => this.branch()?.id);
  public locationId = computed(() => this.location()?.id);

  public canCreateCancellation = computed(() => !!this.branchId() && !!this.locationId() && !!this.locationAlias().trim());
  public canCreateReview = computed(
    () => this.createdCancellationId() !== undefined && !!this.locationId() && this.createdReviewId() === undefined,
  );

  public cancelLimit = signal<number>(5);
  public recentCancellations = signal<KioskTransaction[]>([]);
  public isCancelDateTooClose = computed(() => {
    try {
      const selected = this.combineDateTime(this.cancelDateDate(), this.cancelTime());
      const selectedMs = selected?.getTime?.() ?? NaN;
      if (!Number.isFinite(selectedMs)) return false;
      const recents = this.recentCancellations();
      if (!recents || recents.length === 0) return false;
      return recents.some((c) => Math.abs(selectedMs - new Date(c.trans_date).getTime()) <= 90_000);
    } catch {
      return false;
    }
  });
  public readonly recentCancellationColumns: ColDef<KioskTransaction>[] = [
    {
      field: 'trans_date',
      headerName: 'Cancel Date',
      cellDataType: 'date',
      minWidth: 160,
      sortable: true,
      valueGetter: (p) => {
        const d = p?.data;
        const raw = d?.trans_date;
        if (!raw) return undefined;
        if (raw instanceof Date) return raw;
        const dt = DateTime.fromISO(String(raw), { zone: 'utc' });
        return dt.isValid ? dt.toJSDate() : undefined;
      },
      valueFormatter: (params) => {
        const v = params.value;
        let date: Date | undefined;
        if (v instanceof Date) {
          date = v;
        } else if (typeof v === 'string') {
          const dt = DateTime.fromISO(v, { zone: 'utc' });
          date = dt.isValid ? dt.toJSDate() : undefined;
        } else if (typeof v === 'number') {
          date = new Date(v);
        }
        if (!date || isNaN(date.getTime())) return '';
        return DateTime.fromJSDate(date).toLocal().toFormat('MM/dd/yyyy h:mm:ss a');
      },
    },
    { field: 'location_name', headerName: 'Location', cellDataType: 'text', minWidth: 180, sortable: true },
    { field: 'branch_alias_name', headerName: 'Branch', cellDataType: 'text', minWidth: 180, sortable: true },
    { field: 'trans_id', headerName: 'Transaction ID', cellDataType: 'text', minWidth: 220, sortable: true },
    { field: 'user_key', headerName: 'User', cellDataType: 'text', minWidth: 140, sortable: true },
  ];
  public readonly recentCancellationTable: GridOptions<KioskTransaction> = { ...defaultTableDefOptions() };
  public paginationNumberFormatter = (params: PaginationNumberFormatterParams) => `[${params.value}]`;

  public addItem() {
    const next = [...this.items(), { description: '', price: undefined }];
    this.items.set(next);
  }

  public removeItem(index: number) {
    const next = [...this.items()];
    next.splice(index, 1);
    this.items.set(next);
  }

  public async onPhotoSelected($event: Event, index: number) {
    const input = $event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];
    const file = files?.[0];
    if (!file) return;

    const previewUrl = await this.fileToDataUrl(file).catch(() => undefined);
    const next = [...this.photoRows()];
    next[index] = { file, previewUrl };
    this.photoRows.set(next);
  }

  public onVideoSelected($event: Event) {
    const input = $event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];
    const file = files?.[0] ?? null;
    this.videoFile.set(file);
  }

  public onRecentWalkInsSelected($event: Event) {
    const input = $event.target as HTMLInputElement | null;
    const files = input?.files ? Array.from(input.files) : [];
    this.recentWalkInFiles.set(files);
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(String(e.target?.result ?? ''));
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  private getSelectedPhotoFiles(): File[] {
    return this.photoRows()
      .map((r) => r.file)
      .filter((f): f is File => !!f);
  }

  public onBranchChange(branch: Branch[] | Branch | null) {
    if (!branch) {
      // Only act if branch was previously set
      if (this.branch()) {
        this.branch.set(undefined);
        this.location.set(undefined);
        this.resetAliasSelections();
        this.clearAliasOptions();
        this.lastBranchId.set(undefined);
        this.lastLocationId.set(undefined);
        this.lastBranchAliasId.set(undefined);
        this.lastKioskId.set(undefined);
        this.upsertQueryParams({ branch_id: null, location_id: null, branch_alias_id: null, location_alias_id: null });
        this.createdCancellationId.set(undefined);
        this.createdReviewId.set(undefined);
      }
      return;
    }
    const b = branch as Branch;
    const changed = this.lastBranchId() !== b.id;
    this.branch.set(b);
    if (changed) {
      this.lastBranchId.set(b.id);
      this.location.set(undefined);
      this.resetAliasSelections();
      this.lastLocationId.set(undefined);
      this.lastBranchAliasId.set(undefined);
      this.lastKioskId.set(undefined);

      this.locationSelect().reset();
      this.locationSelect().getLocations(this.branchId());
      this.createdCancellationId.set(undefined);
      this.createdReviewId.set(undefined);

      const preserveAliases = this.shouldPreserveAliasParamsOnInitForBranch(b.id);
      const preserveLocation = !this.hasAppliedQueryParams() && this.branchIdParam() === b.id && this.locationIdParam() !== undefined;
      const qp: { [k: string]: string | number | null | undefined } = { branch_id: b.id };
      if (!preserveLocation) qp.location_id = null;
      if (!preserveAliases) {
        qp.branch_alias_id = null;
        qp.location_alias_id = null;
      }
      this.upsertQueryParams(qp);
    }
    this.loadBranchLocationDetails();
  }

  public onLocationChange(location: LocationTiny[] | LocationTiny | null) {
    if (!location) {
      if (this.location()) {
        this.location.set(undefined);
        this.resetAliasSelections();
        this.recentCancellations.set([]);
        this.createdCancellationId.set(undefined);
        this.createdReviewId.set(undefined);
        this.lastLocationId.set(undefined);
        this.lastBranchAliasId.set(undefined);
        this.lastKioskId.set(undefined);
        this.upsertQueryParams({ location_id: null, branch_alias_id: null, location_alias_id: null });
      }
      return;
    }
    const loc = location as LocationTiny;
    const changed = this.lastLocationId() !== loc.id;
    this.location.set(loc);
    if (changed) {
      this.lastLocationId.set(loc.id);
      this.resetAliasSelections();
      this.lastBranchAliasId.set(undefined);
      this.lastKioskId.set(undefined);
      this.createdCancellationId.set(undefined);
      this.createdReviewId.set(undefined);
      const preserveAliases = this.shouldPreserveAliasParamsOnInitForLocation(loc.id);
      const qp: { [k: string]: string | number | null | undefined } = { location_id: loc.id };
      if (!preserveAliases) {
        qp.branch_alias_id = null;
        qp.location_alias_id = null;
      }
      this.upsertQueryParams(qp);
    }

    this.loadBranchLocationDetails();
  }

  public onPaginationChanged(event: PaginationChangedEvent) {
    const newSize = event.api.paginationGetPageSize();
    if (newSize && newSize !== this.cancelLimit()) {
      this.cancelLimit.set(newSize);
      this.updateRecentCancellationsFromSelection();
    }
  }

  public refreshRecentCancellations() {
    this.updateRecentCancellationsFromSelection();
  }

  public onBranchAliasChange(branchAliasId?: number) {
    const nextId = branchAliasId ?? undefined;
    const changed = this.lastBranchAliasId() !== nextId;
    this.selectedBranchAliasId.set(nextId);
    const selected = this.branchAliasOptions().find((o) => o.id === branchAliasId);
    this.branchAlias.set(selected ? selected.name : '');
    if (changed) {
      this.lastBranchAliasId.set(nextId);
      this.selectedKioskId.set(undefined);
      this.lastKioskId.set(undefined);
      this.locationAlias.set('');
      this.refreshKioskOptions();
      this.updateRecentCancellationsFromSelection();
      this.createdCancellationId.set(undefined);
      this.createdReviewId.set(undefined);
      const preserveLocationAlias =
        !this.hasAppliedQueryParams() && this.branchAliasIdParam() === nextId && this.locationAliasIdParam() !== undefined;
      const qp: { [k: string]: string | number | null | undefined } = { branch_alias_id: branchAliasId ?? null };
      if (!preserveLocationAlias) {
        qp.location_alias_id = null;
      }
      this.upsertQueryParams(qp);
    } else {
      this.refreshKioskOptions();
    }
  }

  public onKioskChange(kioskId?: number) {
    const nextId = kioskId ?? undefined;
    const changed = this.lastKioskId() !== nextId;
    this.selectedKioskId.set(nextId);
    const selected = this.locationAliasOptions().find((o) => o.id === kioskId);
    this.locationAlias.set(selected ? selected.name : '');
    if (changed) {
      this.lastKioskId.set(nextId);
      this.updateRecentCancellationsFromSelection();
      this.upsertQueryParams({ location_alias_id: kioskId ?? null });
      this.createdCancellationId.set(undefined);
      this.createdReviewId.set(undefined);
    }
  }

  private updateRecentCancellationsFromSelection() {
    const branchId = this.branchId();
    const locationId = this.locationId();
    const branchAliasId = this.selectedBranchAliasId();
    const kioskId = this.selectedKioskId();
    if (!branchId || !locationId || !branchAliasId || !kioskId) {
      this.recentCancellations.set([]);
      return;
    }
    this.isLoadingRecent.set(true);
    this.branchService
      .getBranchKioskMostRecentTransactions(branchId, kioskId)
      .pipe(finalize(() => this.isLoadingRecent.set(false)))
      .subscribe({
        next: (response) => {
          const cancellations = (response?.items ?? [])
            .filter((item) => item?.type === 'cancellation')
            .sort((a, b) => new Date(b.trans_date).getTime() - new Date(a.trans_date).getTime())
            .slice(0, this.cancelLimit());
          this.recentCancellations.set(cancellations);
        },
        error: (err) => {
          this.recentCancellations.set([]);
          this.notify.notify(tryGetErrorMessage(err));
        },
      });
  }

  private refreshKioskOptions() {
    const aliasId = this.selectedBranchAliasId();
    const kiosks = this.kiosks();
    const filtered = aliasId ? kiosks.filter((k) => k.branch_alias_id === aliasId) : kiosks;
    const opts = filtered.map((k) => ({
      id: k.id,
      name: k.name,
      branch_alias_id: k.branch_alias_id,
      branch_alias_name: k.branch_alias_name,
    }));
    this.locationAliasOptions.set(opts);
  }

  private resetAliasSelections() {
    this.branchAlias.set('');
    this.locationAlias.set('');
    this.selectedBranchAliasId.set(undefined);
    this.selectedKioskId.set(undefined);
    this.clearAliasOptions();
  }

  private clearAliasOptions() {
    this.branchAliasOptions.set([]);
    this.locationAliasOptions.set([]);
    this.isLoadingBranchAlias.set(false);
    this.isLoadingLocationAlias.set(false);
  }

  private loadBranchLocationDetails() {
    const branchId = this.branchId();
    const locationId = this.locationId();
    if (!branchId || !locationId) {
      this.kiosks.set([]);
      this.clearAliasOptions();
      this.recentCancellations.set([]);
      this.createdCancellationId.set(undefined);
      this.createdReviewId.set(undefined);
      return;
    }
    this.locationService
      .getLocation({ locationId: locationId, options: { include_kiosks: true } })
      .pipe(finalize(() => {}))
      .subscribe(
        (resp) => {
          const kiosks = resp.kiosks ?? [];
          this.kiosks.set(kiosks);

          const branchAliasMap = new Map<number, string>();
          for (const k of kiosks) {
            if (k.branch_alias_id && !branchAliasMap.has(k.branch_alias_id)) {
              branchAliasMap.set(k.branch_alias_id, k.branch_alias_name);
            }
          }
          const branchAliasOptions = Array.from(branchAliasMap.entries()).map(([id, name]) => ({ id, name }));
          this.branchAliasOptions.set(branchAliasOptions);
          this.refreshKioskOptions();
          if (!this.hasAppliedQueryParams()) {
            const desiredBranchAliasId = this.branchAliasIdParam();
            if (desiredBranchAliasId) {
              this.onBranchAliasChange(desiredBranchAliasId);
            }
            const desiredKioskId = this.locationAliasIdParam();
            if (desiredKioskId) {
              this.onKioskChange(desiredKioskId);
            }
            this.hasAppliedQueryParams.set(true);
          }
        },
        () => {
          this.kiosks.set([]);
          this.clearAliasOptions();
          this.recentCancellations.set([]);
        },
      );
  }

  public generateUuid(): string {
    const uuid = generateUuidUtil();
    this.uniqueId.set(uuid);
    return uuid;
  }

  private getOrCreateUniqueId(): string {
    const current = this.uniqueId().trim();
    if (current) return current;
    return this.generateUuid();
  }

  public createCancellation() {
    if (!this.canCreateCancellation() || this.isCreatingCancellation()) return;
    const uniqueId = this.getOrCreateUniqueId();
    const cancelDateIso = this.combineDateTime(this.cancelDateDate(), this.cancelTime()).toISOString();
    const payload: CreateCancellationRequest = {
      branch_id: this.branchId()!,
      branch_alias: this.branchAlias().trim() || this.branch()?.name || '',
      location_id: this.locationId()!,
      location_alias: this.locationAlias().trim(),
      unique_id: uniqueId,
      cancel_date: cancelDateIso,
      theft_score: this.theftScore() ?? 1,
      theft_category: this.theftCategory(),
      face_similarity: this.faceSimilarity() ? 1 : 0,
    };

    const items = this.items().filter((i) => (i.description && i.description.trim() !== '') || i.price !== undefined);
    if (items.length > 0) {
      payload.items = items.map((i) => ({
        description: i.description || undefined,
        price: this.parseNumber(i.price),
      })) as CreateCancellationItemRequest[];
    }

    const imageFiles = this.getSelectedPhotoFiles();

    this.isCreatingCancellation.set(true);
    of(null)
      .pipe(
        concatMap(() => iif(() => imageFiles.length > 0, this.lpService.uploadFiles(imageFiles, uniqueId + '.jpg'), of(null))),
        concatMap(() => this.lpService.createCancellation(payload)),
        finalize(() => this.isCreatingCancellation.set(false)),
      )
      .subscribe(
        (response) => {
          this.createdCancellationId.set(response?.id);
          this.createdReviewId.set(undefined);
          this.notify.notify('Cancellation created successfully');
        },
        (err) => this.notify.notify(tryGetErrorMessage(err)),
      );
  }

  public createReview() {
    if (!this.canCreateReview() || this.isCreatingReview()) return;
    const locationId = this.locationId();
    if (!locationId) return;
    const uniqueId = this.uniqueId().trim();
    if (!uniqueId) {
      this.notify.notify('Unique ID is required before creating the review');
      return;
    }

    const video = this.videoFile();
    const recentFiles = this.recentWalkInFiles();
    const videoOffset = this.videoOffsetSeconds();

    this.isCreatingReview.set(true);
    of(null)
      .pipe(
        concatMap(() => this.assignmentService.createAssignment(locationId, '', ReviewSubjectType.Cancellation, true)),
        concatMap((assignment: ReviewAssignmentDto) =>
          iif(
            () => !!video,
            this.lpService
              .uploadVideoClips([video!], {
                filePath: uniqueId + '.mp4',
                cancellationUniqueId: uniqueId,
                reviewId: assignment.review_id,
                cancellationOffsetSeconds: videoOffset ?? 0,
              })
              .pipe(map((clips) => ({ assignment, videoClipId: clips?.[0]?.id }))),
            of({ assignment }),
          ),
        ),
        concatMap((state: { assignment: ReviewAssignmentDto; videoClipId?: string }) =>
          iif(
            () => recentFiles.length > 0 && !!state.videoClipId,
            this.lpService
              .uploadFiles(
                recentFiles,
                undefined,
                recentFiles.map((f) => {
                  const original = typeof f.name === 'string' ? f.name : 'image';
                  const base = original.replace(/\.[^.]+$/, '');
                  const safe = base.toLowerCase().replace(/[^a-z0-9_-]+/g, '-');
                  return `${uniqueId}-${safe}.jpg`;
                }),
              )
              .pipe(
                concatMap((links: string[]) => {
                  const videoClipId = state.videoClipId!;
                  const video_snapshots = links.map((link) => ({
                    link,
                    image_timestamp: Date.now(),
                    video_clip_id: videoClipId,
                  }));
                  return this.lpService.addVideoSnapshots(uniqueId, { video_snapshots });
                }),
                map(() => state),
              ),
            of(state),
          ),
        ),
        finalize(() => this.isCreatingReview.set(false)),
      )
      .subscribe(
        ({ assignment }) => {
          this.createdReviewId.set(assignment.review_id);
          this.notify.notify('Review created successfully');
          this.uniqueId.set('');
          this.recentWalkInFiles.set([]);
        },
        (err) => this.notify.notify(tryGetErrorMessage(err)),
      );
  }

  public onDateChange(date: Date) {
    if (!date) return;
    const combined = this.combineDateTime(date, this.cancelTime());
    this.cancelDateDate.set(combined);
  }

  public onTimeChange(time: string) {
    if (!time) return;
    const combined = this.combineDateTime(this.cancelDateDate(), time);
    this.cancelDateDate.set(combined);
    this.cancelTime.set(this.formatTime(combined));
  }

  public setNow() {
    const now = new Date();
    this.cancelDateDate.set(now);
    this.cancelTime.set(this.formatTime(now));
  }

  public add90Seconds() {
    const current = this.combineDateTime(this.cancelDateDate(), this.cancelTime());
    const next = new Date(current.getTime() + 90_000);
    this.cancelDateDate.set(next);
    this.cancelTime.set(this.formatTime(next));
  }

  private combineDateTime(date: Date, time: string): Date {
    const [h, m, s] = this.parseTime(time);
    const combined = new Date(date);
    combined.setHours(h, m, s ?? 0, 0);
    return combined;
  }

  private formatTime(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  private parseTime(time: string): [number, number, number] {
    const parts = time.split(':').map((p) => parseInt(p, 10));
    const h = parts[0] || 0;
    const m = parts[1] || 0;
    const s = parts[2] || 0;
    return [h, m, s];
  }

  ngOnInit(): void {
    const query = this.route.snapshot.queryParams as Record<string, string>;
    const branchId = Number(query['branch_id']);
    const locationId = Number(query['location_id']);
    const branchAliasId = Number(query['branch_alias_id']);
    const locationAliasId = Number(query['location_alias_id']);
    this.branchIdParam.set(Number.isFinite(branchId) && branchId > 0 ? branchId : undefined);
    this.locationIdParam.set(Number.isFinite(locationId) && locationId > 0 ? locationId : undefined);
    this.branchAliasIdParam.set(Number.isFinite(branchAliasId) && branchAliasId > 0 ? branchAliasId : undefined);
    this.locationAliasIdParam.set(Number.isFinite(locationAliasId) && locationAliasId > 0 ? locationAliasId : undefined);
  }

  private parseNumber(v: number | string | null | undefined): number | undefined {
    if (v === null || v === undefined || (typeof v === 'string' && v === '')) return undefined;
    if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
    if (typeof v === 'string') {
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  }

  private upsertQueryParams(params: { [k: string]: string | number | null | undefined }) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: params,
    });
  }
}
